import type { CSSProperties } from 'react'

declare module 'react' {
  // Not in CSSProperties' types yet.
  interface CSSProperties {
    viewTransitionName?: string | undefined
  }
}

export const prefersReducedMotion = () =>
  typeof matchMedia === 'function' &&
  matchMedia('(prefers-reduced-motion: reduce)').matches

/**
 * Runs a DOM update inside a view transition, so the browser morphs elements
 * sharing a `view-transition-name` between their old and new boxes. Browsers
 * without the API (and reduced-motion users) get the update straight away.
 * `update` must apply its changes synchronously, e.g. via React's `flushSync`.
 */
export const withViewTransition = (
  update: () => void,
  /** Runs once the change has settled: after the transition, or straight away. */
  after?: () => void
) => {
  if (
    typeof document.startViewTransition !== 'function' ||
    prefersReducedMotion()
  ) {
    update()
    after?.()
    return
  }
  // A transition skipped by a newer one rejects these; nothing to handle.
  const transition = document.startViewTransition(update)
  transition.ready.catch(() => undefined)
  transition.finished.then(
    () => after?.(),
    () => after?.()
  )
}

/** The style giving an element a `view-transition-name`. */
export const viewTransitionStyle = (name: string): CSSProperties => ({
  viewTransitionName: name,
})

/** Shared by a card's tile and its expanded panel, so one morphs into the other. */
export const cardTransitionName = (cardId: number) => `ln-card-${cardId}`

/** The id of a tile's expand button, to give it focus back on collapse. */
export const tileButtonId = (cardId: number) => `tile-expand-${cardId}`
